/**
 * Daisy tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_subjects_ii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const daisyTattoosPack: ToilePack = {
    slug: 'daisy-tattoos',
    category: 'tattoo',
    name: 'Daisy tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'stem', label: 'Single daisy', subject: 'a single daisy on a thin stem with one leaf, the round centre dotted and the petals open'},
        {key: 'chain', label: 'Daisy chain', subject: 'a daisy chain of linked stems and small flowers curving in a loose arc'},
        {key: 'bunch', label: 'Daisy bunch', subject: 'three daisies of different heights tied together with a thin ribbon at the stems'},
    ],
    styles: [
        {key: 'fineline', label: 'Fine line', style: 'a single-needle fine line tattoo drawing, one uniform hairline stroke, delicate detail and almost no shading', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'dotwork', label: 'Dotwork', style: 'a dotwork tattoo drawing, a thin outline with all shading built from stippled dots and dot gradients', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'traditional', label: 'Traditional', style: 'American traditional tattoo flash, bold black outlines, heavy black shading and a limited flat palette', palette: 'red, yellow and green accents with black', framing: 'centered with a wide white margin'},
    ],
    directions: ['a bee', 'yellow ink', 'ankle'],
    directionHint: 'try: a bee',
    seo: {
        title: 'Daisy tattoos: single daisy, daisy chain, bunch',
        metaDescription: 'Nine daisy tattoo designs: one daisy on its stem, a daisy chain and a small tied bunch, in fine line, dotwork and traditional.',
        h1: 'Daisy tattoo designs: a single daisy, a daisy chain, a bunch',
        tagline: 'Three designs, each drawn in fine line, dotwork and American traditional on white paper.',
        intro: 'The daisy is a small, cheerful flower, worn for innocence and new starts, and usually placed small on the wrist, the finger or the ankle. These nine show one daisy on its stem, a daisy chain curving in an arc, and three daisies tied with a ribbon. Each is drawn as fine line, dotwork or American traditional. Type a different flower or a twist, such as a bee on the petals, and the three styles carry the request.',
        useCases: [
            'a small wrist or ankle design to take to an artist',
            'a birthday card or gift tag motif',
            "a child's room print",
            'a drawing to trace onto paper',
        ],
        faq: [
            {q: 'What is in the nine designs?', a: 'A single daisy on a thin stem, a daisy chain of linked stems and flowers, and a small bunch of three daisies tied with a ribbon. Each is drawn in three styles.'},
            {q: 'Can I print these?', a: 'Each design sits on a plain white background in dark line work, so it reads clearly on paper.'},
            {q: 'What is the difference between the three styles?', a: 'Fine line is one uniform hairline with almost no shading. Dotwork builds all its shading from dots. American traditional has bold black outlines, heavy shading and flat red, yellow and green.'},
            {q: 'Can I ask for a different subject?', a: 'Yes. Type the subject or twist you want, such as a bee on the petals. The nine shown are samples, and a request is not a promised result.'},
        ],
    },
}
